<template>
    <div class="flex flex-col bg-gray-100 text-gray-900 overflow-hidden select-none relative" ref="root">

        <!-- ═══ TOP BAR (desktop) ═══ -->
        <header class="hidden md:flex items-center gap-2.5 px-3.5 h-12 bg-white border-b border-gray-200 flex-shrink-0 z-20 shadow-[0_1px_6px_rgba(0,0,0,0.03)]">
            <!-- View switcher -->
            <div class="flex items-center gap-0.5 bg-gray-100 rounded-[9px] p-[3px] border border-gray-200 flex-shrink-0">
                <button :class="['flex items-center gap-[5px] px-3 py-[5px] rounded-[7px] border-none cursor-pointer text-xs font-medium transition-all', viewMode === 'day' ? 'bg-white text-gray-900 font-bold shadow-sm' : 'bg-transparent text-gray-400 hover:text-gray-700']" @click="viewMode = 'day'">
                    <i class="fas fa-calendar-day"></i><span>Jour</span>
                </button>
                <button :class="['flex items-center gap-[5px] px-3 py-[5px] rounded-[7px] border-none cursor-pointer text-xs font-medium transition-all', viewMode === 'week' ? 'bg-white text-gray-900 font-bold shadow-sm' : 'bg-transparent text-gray-400 hover:text-gray-700']" @click="viewMode = 'week'">
                    <i class="fas fa-calendar-week"></i><span>Semaine</span>
                </button>
                <button :class="['flex items-center gap-[5px] px-3 py-[5px] rounded-[7px] border-none cursor-pointer text-xs font-medium transition-all', viewMode === 'month' ? 'bg-white text-gray-900 font-bold shadow-sm' : 'bg-transparent text-gray-400 hover:text-gray-700']" @click="viewMode = 'month'">
                    <i class="fas fa-calendar"></i><span>Mois</span>
                </button>
            </div>

            <!-- Date nav (desktop) -->
            <div class="flex items-center gap-0.5 ml-auto" ref="dateNav" data-date-nav>
                <button class="w-7 h-7 rounded-[7px] border border-gray-200 bg-white flex items-center justify-center cursor-pointer text-gray-500 text-[10px] transition-all hover:border-indigo-400 hover:text-indigo-500 hover:bg-indigo-50" @click="offsetPeriod(-1)"><i class="fas fa-chevron-left"></i></button>
                <button class="tb-date-trigger flex items-center gap-[7px] px-3 h-7 rounded-lg border border-gray-200 bg-white cursor-pointer text-[12.5px] font-semibold text-gray-800 whitespace-nowrap transition-all hover:border-indigo-400 hover:shadow-[0_0_0_3px_rgba(99,102,241,0.1)]" @click="toggleDatePicker">
                    <i class="fas fa-calendar-alt text-indigo-500 text-xs"></i>
                    <span>{{ dateRangeLabel }}</span>
                    <i class="fas fa-chevron-down text-[9px] text-gray-400 transition-transform" :class="{ 'rotate-180': datePickerOpen }"></i>
                </button>
                <button class="w-7 h-7 rounded-[7px] border border-gray-200 bg-white flex items-center justify-center cursor-pointer text-gray-500 text-[10px] transition-all hover:border-indigo-400 hover:text-indigo-500 hover:bg-indigo-50" @click="offsetPeriod(1)"><i class="fas fa-chevron-right"></i></button>
            </div>

            <!-- Tech filter (desktop) -->
            <div v-if="technicians.length > 1" class="flex items-center gap-1 flex-1" ref="techFilter">
                <div class="relative" :class="{ open: selectOpen }" @mouseenter="onEnter" @mouseleave="onLeave">
                    <div class="flex items-center gap-2 px-3 py-2 bg-white border-[1.5px] border-gray-200 rounded-[10px] cursor-pointer min-w-[200px] whitespace-nowrap transition-all hover:border-indigo-400 hover:shadow-[0_0_0_3px_rgba(99,102,241,0.1)]" ref="csTrigger">
                        <span class="w-2.5 h-2.5 rounded-full flex-shrink-0 inline-block" :style="selectedTech ? { background: selectedTech.color } : { background: 'linear-gradient(135deg,#6366f1,#ec4899,#f59e0b)' }"></span>
                        <span class="text-gray-800 font-medium text-sm flex-1">{{ selectedTech ? selectedTech.name : 'Tous les techniciens' }}</span>
                        <i class="fas fa-chevron-down text-[0.7rem] text-gray-400 transition-transform ml-auto" :class="{ 'rotate-180': selectOpen }"></i>
                    </div>
                </div>
                <div v-if="selectOpen" class="bg-white border-[1.5px] border-gray-200 rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.13)] overflow-hidden" :style="dropdownStyle" @mouseenter="onEnter" @mouseleave="onLeave">
                    <div :class="['flex items-center gap-2.5 px-3.5 py-2.5 cursor-pointer text-sm text-gray-600 transition-colors whitespace-nowrap hover:bg-gray-50', filterTechId === null ? 'bg-indigo-50 text-indigo-600 font-semibold' : '']" @click="filterTechId = null; selectOpen = false">
                        <span class="w-2.5 h-2.5 rounded-full flex-shrink-0 inline-block" style="background:linear-gradient(135deg,#6366f1,#ec4899,#f59e0b)"></span>
                        <span>Tous les techniciens</span>
                    </div>
                    <div v-for="t in technicians" :key="'tfc-'+t.id" :class="['flex items-center gap-2.5 px-3.5 py-2.5 cursor-pointer text-sm text-gray-600 transition-colors whitespace-nowrap hover:bg-gray-50', filterTechId === t.id ? 'bg-indigo-50 text-indigo-600 font-semibold' : '']" @click="filterTechId = t.id; selectOpen = false">
                        <span class="w-2.5 h-2.5 rounded-full flex-shrink-0 inline-block" :style="{ background: t.color }"></span>
                        <span>{{ t.name }}</span>
                    </div>
                </div>
            </div>

            <!-- Multi-slot badge (desktop) -->
            <transition name="ms-badge-slide">
                <div v-if="!isMobile && multiSlots && selectedSlots.length > 0" class="flex items-center gap-1.5 px-2.5 py-[5px] rounded-[9px] bg-gradient-to-br from-indigo-600 to-indigo-500 text-white flex-shrink-0 shadow-[0_2px_8px_rgba(79,70,229,0.35)] text-xs font-semibold">
                    <i class="fas fa-layer-group text-[11px] opacity-85"></i>
                    <span class="bg-white/25 rounded px-1.5 py-0.5 text-[11px] font-extrabold">{{ selectedSlots.length }}</span>
                    <span class="text-[11.5px]">slot{{ selectedSlots.length > 1 ? 's' : '' }} sélectionné{{ selectedSlots.length > 1 ? 's' : '' }}</span>
                    <button class="w-[18px] h-[18px] rounded-full border-none bg-white/20 text-white cursor-pointer flex items-center justify-center text-[9px] ml-0.5 hover:bg-white/35 transition-colors" @click="clearSelection"><i class="fas fa-times"></i></button>
                </div>
            </transition>
        </header>

        <!-- ═══ MOBILE HEADER ═══ -->
        <div class="md:hidden flex-shrink-0 bg-white z-20 mob-header" data-date-nav>

            <!-- Navigation date -->
            <div class="mob-date-row">
                <button class="mob-arrow-btn" @click="offsetPeriod(-1)">
                    <i class="fas fa-chevron-left"></i>
                </button>
                <button ref="mobileDateBtn" class="mob-date-center" @click="toggleDatePicker">
                    <span class="mob-date-month-label">{{ mobMonth }}</span>
                    <span class="mob-date-day-num">{{ mobDayNum }}</span>
                    <span class="mob-date-weekday">{{ mobWeekday }}</span>
                </button>
                <button class="mob-arrow-btn" @click="offsetPeriod(1)">
                    <i class="fas fa-chevron-right"></i>
                </button>
            </div>

            <!-- Aujourd'hui -->
            <div v-if="!isToday" class="mob-today-row">
                <button class="mob-today-btn" @click="goTodayDirect">Aller à aujourd'hui</button>
            </div>

            <!-- Chips techniciens -->
            <div v-if="technicians.length > 0" class="mob-chips-row">
                <button v-if="technicians.length > 1"
                    class="mob-chip"
                    :class="filterTechId === null ? 'mob-chip-active' : ''"
                    @click="filterTechId = null">Tous</button>
                <button v-for="t in technicians" :key="'mchip-'+t.id"
                    class="mob-chip"
                    :class="filterTechId === t.id ? 'mob-chip-active' : ''"
                    :style="filterTechId === t.id ? { background: t.color, borderColor: t.color } : {}"
                    @click="filterTechId = t.id">
                    <span class="mob-chip-dot" :style="filterTechId === t.id ? { background: 'rgba(255,255,255,0.35)' } : { background: t.color }">{{ initials(t.name) }}</span>
                    {{ t.name.split(' ')[0] }}
                </button>
            </div>

            <!-- Badge multi-slot -->
            <transition name="ms-badge-slide">
                <div v-if="multiSlots && selectedSlots.length > 0" class="mob-ms-bar">
                    <i class="fas fa-check-circle" style="color:#4ade80"></i>
                    <span>{{ selectedSlots.length }} créneau{{ selectedSlots.length > 1 ? 'x' : '' }} sélectionné{{ selectedSlots.length > 1 ? 's' : '' }}</span>
                    <button class="mob-ms-clear" @click="clearSelection"><i class="fas fa-times"></i></button>
                </div>
            </transition>

            <div class="mob-header-line"></div>
        </div>

        <!-- ══════════════════ VUE MOBILE ══════════════════ -->
        <div v-if="isMobile" class="mob-scroll">

            <!-- CAS : Tous les techs -->
            <template v-if="!mobileSingleTech && filteredTechnicians.length > 1">
                <p class="mob-section-label">Choisir un technicien</p>
                <div class="mob-tech-list">
                    <div v-for="tech in filteredTechnicians" :key="'mall-'+tech.id"
                        class="mob-tech-row"
                        @click="filterTechId = tech.id">
                        <div class="mob-tech-avatar" :style="{ background: tech.color || '#3b82f6' }">{{ initials(tech.name) }}</div>
                        <div class="mob-tech-info">
                            <div class="mob-tech-name">{{ tech.name }}</div>
                            <div class="mob-tech-slots">
                                <span v-for="slot in halfDaySlots" :key="'msum-'+slot.id">
                                    {{ slot.id === 'morning' ? 'Matin' : 'AM' }}
                                    <b :style="isOverloaded(tech.id, selectedDate, slot.id) ? 'color:#ef4444' : 'color:#111'">{{ getEventsForCell(tech.id, selectedDate, slot.id).filter(e=>!e._isPreview).length }}</b>
                                </span>
                            </div>
                        </div>
                        <i class="fas fa-chevron-right mob-tech-arrow"></i>
                    </div>
                </div>
            </template>

            <!-- CAS : Un tech -->
            <template v-else>
                <div v-for="slot in halfDaySlots" :key="'mobslot-'+slot.id" class="mob-slot-card">

                    <!-- En-tête -->
                    <div class="mob-slot-head">
                        <div>
                            <div class="mob-slot-title">{{ slot.id === 'morning' ? 'Matin' : 'Après-midi' }}</div>
                            <div class="mob-slot-hours">{{ slot.id === 'morning' ? heureDebutMatin + ' – ' + heurefinMatin : heureDebutApresMidi + ' – ' + heureFinApresMidi }}</div>
                        </div>
                        <span v-if="isOverloaded(mobTechId, selectedDate, slot.id)" class="mob-badge mob-badge-red">
                            <i class="fas fa-exclamation-triangle"></i> Surcharge
                        </span>
                        <span v-else-if="mobEventCount(slot.id) === 0" class="mob-badge mob-badge-green">
                            <i class="fas fa-check"></i> Libre
                        </span>
                    </div>

                    <!-- Tabs pill -->
                    <div class="mob-tabs">
                        <button class="mob-tab" :class="mobActiveTab[slot.id] !== 'list' ? 'mob-tab-active' : ''" @click="setMobTab(slot.id, 'recap')">Récap</button>
                        <button class="mob-tab" :class="mobActiveTab[slot.id] === 'list' ? 'mob-tab-active' : ''" @click="setMobTab(slot.id, 'list')">
                            Interventions
                            <span v-if="mobEventCount(slot.id) > 0" class="mob-tab-badge" :class="mobActiveTab[slot.id] === 'list' ? 'mob-tab-badge-active' : ''">{{ mobEventCount(slot.id) }}</span>
                        </button>
                    </div>

                    <!-- Récap -->
                    <div v-if="mobActiveTab[slot.id] !== 'list'" class="mob-recap">
                        <p v-if="mobEventCount(slot.id) === 0" class="mob-recap-empty">Aucune intervention prévue</p>
                        <div v-else class="mob-stats">
                            <div class="mob-stat">
                                <span class="mob-stat-num">{{ mobEventCount(slot.id) }}</span>
                                <span class="mob-stat-lbl">intervention{{ mobEventCount(slot.id) > 1 ? 's' : '' }}</span>
                            </div>
                            <div class="mob-stat-sep"></div>
                            <div class="mob-stat">
                                <span class="mob-stat-num" :style="isOverloaded(mobTechId, selectedDate, slot.id) ? 'color:#ef4444' : ''">
                                    {{ isOverloaded(mobTechId, selectedDate, slot.id) ? 'Saturé' : (formatDuration(slotRemainingMins(mobTechId, selectedDate, slot.id)) || '—') }}
                                </span>
                                <span class="mob-stat-lbl">disponible</span>
                            </div>
                        </div>
                    </div>

                    <!-- Liste -->
                    <div v-if="mobActiveTab[slot.id] === 'list'" class="mob-ev-list">
                        <div v-if="getEventsForCell(mobTechId, selectedDate, slot.id).length === 0" class="mob-ev-empty">Aucune intervention</div>
                        <div v-for="ev in getEventsForCell(mobTechId, selectedDate, slot.id)" :key="'mev-'+ev.id"
                            class="mob-ev-item"
                            :style="ev._isPreview ? 'opacity:0.55' : ''"
                            @click.stop="openEventMenu($event, ev)">
                            <div class="mob-ev-dot" :class="statusDotTw(ev.status || 'planned')"></div>
                            <div class="mob-ev-body">
                                <div class="mob-ev-title">{{ ev.title }}</div>
                                <div class="mob-ev-sub" v-if="ev.ville || ev.estimatedDuration || ev.duration">
                                    <span v-if="ev.ville">{{ ev.ville }}</span>
                                    <span v-if="ev.ville && (ev.estimatedDuration || ev.duration)" class="mob-ev-sep">·</span>
                                    <span v-if="ev.estimatedDuration || ev.duration">{{ formatDuration(ev.estimatedDuration || ev.duration) }}</span>
                                </div>
                            </div>
                            <i class="fas fa-chevron-right mob-ev-arrow"></i>
                        </div>
                    </div>

                    <!-- Action -->
                    <div class="mob-slot-footer">
                        <button v-if="!readonly && !multiSlots" class="mob-add-btn" @click.stop="$emit('new-event', { techId: mobTechId, date: selectedDate, slot: slot.id })">
                            <span class="mob-add-icon"><i class="fas fa-plus"></i></span>
                            Ajouter une intervention
                        </button>
                        <button v-if="multiSlots" class="mob-select-btn" :class="isSlotSelected(mobTechId, selectedDate, slot.id) ? 'mob-select-btn-on' : ''" @click.stop="toggleSlot(mobTechId, selectedDate, slot.id)">
                            <i :class="isSlotSelected(mobTechId, selectedDate, slot.id) ? 'fas fa-check-circle' : 'far fa-circle'"></i>
                            {{ isSlotSelected(mobTechId, selectedDate, slot.id) ? 'Créneau sélectionné' : 'Sélectionner ce créneau' }}
                        </button>
                    </div>
                </div>
                <div style="height:40px"></div>
            </template>
        </div>

        <!-- ══════════════════ VUE SEMAINE (desktop) ══════════════════ -->
        <div v-if="viewMode === 'week' && !isMobile" class="flex-1 overflow-hidden flex flex-col">
            <div class="flex-1 overflow-x-auto overflow-y-auto cursor-grab" :class="{ 'cursor-grabbing': isPanning }" ref="weekScroll" @mousedown="onPanStart">
                <div class="flex flex-col bg-white min-w-[860px]">
                    <!-- Header jours -->
                    <div class="flex border-b border-gray-200 flex-shrink-0 bg-gray-50">
                        <div class="w-40 flex-shrink-0 flex items-center px-3 py-2.5 border-r border-gray-200">
                            <span class="text-[10px] font-bold uppercase tracking-[1.2px] text-gray-300">Techniciens</span>
                        </div>
                        <div v-for="day in weekDays" :key="'wkh-'+day.date"
                            class="flex-1 min-w-[110px] px-1.5 pt-3.5 pb-3.5 border-r border-gray-200 last:border-r-0 text-center cursor-pointer transition-colors"
                            :class="[isTodayDate(day.date) ? 'bg-blue-50' : day.isWeekend ? 'bg-gray-50/70' : 'hover:bg-blue-50/50']"
                            @click="!isPanning && (selectDate(day.date), viewMode='day')">
                            <div class="text-[10px] font-bold uppercase tracking-[0.8px] text-gray-400">{{ day.shortName }}</div>
                            <div class="text-[18px] font-bold leading-tight my-0.5" :class="isTodayDate(day.date) ? 'text-blue-500' : 'text-gray-900'">{{ day.num }}</div>
                            <div class="flex justify-center gap-1.5">
                                <span class="text-[9px] font-bold uppercase text-amber-600">M</span>
                                <span class="text-[9px] font-bold uppercase text-blue-500">AP</span>
                            </div>
                        </div>
                    </div>
                    <!-- Rows tech -->
                    <div v-for="tech in filteredTechnicians" :key="'wkr-'+tech.id" class="flex border-b border-gray-100 min-h-[220px]">
                        <div class="w-40 flex-shrink-0 flex items-center gap-2.5 px-3 py-4 border-r border-gray-100 bg-white sticky left-0 z-10" :style="{ borderLeft: `3px solid ${tech.color || '#3b82f6'}` }">
                            <div class="flex flex-col min-w-0">
                                <div class="text-[12px] font-bold text-gray-900 truncate">{{ tech.name }}</div>
                                <div class="text-[10px] text-gray-400 font-medium">Technicien</div>
                            </div>
                        </div>
                        <!-- Cells jour -->
                        <div v-for="day in weekDays" :key="'wkc-'+tech.id+'-'+day.date"
                            class="flex-1 min-w-[110px] flex flex-col border-r border-gray-100 last:border-r-0 cursor-pointer relative"
                            :class="isTodayDate(day.date) ? 'bg-blue-50/40' : day.isWeekend ? 'bg-gray-50/60' : ''"
                            @click="!isPanning && !multiSlots && (selectDate(day.date), viewMode='day')">
                            <div class="flex flex-1">
                                <!-- Matin -->
                                <div class="flex-1 flex flex-col relative overflow-hidden border-r border-amber-100 transition-colors"
                                    :class="[wkHalfBg(tech.id, day.date, 'morning'), multiSlots && isSlotSelected(tech.id, day.date, 'morning') ? '!bg-indigo-50 outline outline-2 outline-indigo-500 outline-offset-[-2px]' : '']"
                                    @click.stop="!isPanning && (multiSlots ? toggleSlot(tech.id, day.date, 'morning') : !readonly && $emit('new-event', { techId: tech.id, date: day.date, slot: 'morning' }))">
                                    <div class="h-1 bg-gray-100 flex-shrink-0"><div class="h-full" :style="satBarStyle(tech.id, day.date, 'morning')"></div></div>
                                    <div v-if="multiSlots && isSlotSelected(tech.id, day.date, 'morning')" class="absolute inset-0 flex items-center justify-center z-[4] pointer-events-none bg-indigo-500/10">
                                        <div class="w-5 h-5 rounded-full bg-indigo-600 text-white text-[10px] flex items-center justify-center shadow-sm"><i class="fas fa-check"></i></div>
                                    </div>
                                    <div class="flex-1 flex flex-col gap-1.5 px-2 pt-2.5">
                                        <div v-for="ev in getEventsForCell(tech.id, day.date, 'morning')" :key="'wka-'+ev.id"
                                            class="rounded-lg px-2.5 py-2 cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 border-l-[3px] mb-0.5"
                                            :class="ev._isPreview ? 'border-dashed opacity-60' : ''"
                                            :style="wkChipStyle(ev, tech)"
                                            @click.stop="!isPanning && openEventMenu($event, ev)">
                                            <span class="text-[11px] font-semibold text-gray-800 truncate block leading-snug">{{ ev.title }}</span>
                                            <span v-if="ev.ville" class="text-[10px] text-gray-500 truncate block">{{ ev.ville }}</span>
                                        </div>
                                    </div>
                                    <div v-if="isOverloaded(tech.id, day.date, 'morning')" class="absolute bottom-0.5 right-0.5 text-[9px] text-red-500"><i class="fas fa-exclamation-triangle"></i></div>
                                    <button v-if="!readonly"     class="flex-shrink-0 w-6 h-6 rounded-full border border-dashed border-gray-300 bg-white text-gray-400 flex items-center justify-center cursor-pointer text-[11px] mx-auto mb-2.5 transition-all hover:bg-indigo-500 hover:text-white hover:border-indigo-500 hover:scale-110 hover:shadow-sm"
                                        @click.stop="!isPanning && (multiSlots ? toggleSlot(tech.id, day.date, 'morning') : $emit('new-event', { techId: tech.id, date: day.date, slot: 'morning' }))"><i class="fas fa-plus"></i></button>
                                </div>
                                <!-- Après-midi -->
                                <div class="flex-1 flex flex-col relative overflow-hidden transition-colors"
                                    :class="[wkHalfBg(tech.id, day.date, 'afternoon'), multiSlots && isSlotSelected(tech.id, day.date, 'afternoon') ? '!bg-indigo-50 outline outline-2 outline-indigo-500 outline-offset-[-2px]' : '']"
                                    @click.stop="!isPanning && (multiSlots ? toggleSlot(tech.id, day.date, 'afternoon') : !readonly && $emit('new-event', { techId: tech.id, date: day.date, slot: 'afternoon' }))">
                                    <div class="h-1 bg-gray-100 flex-shrink-0"><div class="h-full" :style="satBarStyle(tech.id, day.date, 'afternoon')"></div></div>
                                    <div v-if="multiSlots && isSlotSelected(tech.id, day.date, 'afternoon')" class="absolute inset-0 flex items-center justify-center z-[4] pointer-events-none bg-indigo-500/10">
                                        <div class="w-5 h-5 rounded-full bg-indigo-600 text-white text-[10px] flex items-center justify-center shadow-sm"><i class="fas fa-check"></i></div>
                                    </div>
                                    <div class="flex-1 flex flex-col gap-1.5 px-2 pt-2.5">
                                        <div v-for="ev in getEventsForCell(tech.id, day.date, 'afternoon')" :key="instanceId + '_m_' + ev.id + '_' + tech.id + '_' + day.date"
                                            class="rounded-lg px-2.5 py-2 cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 border-l-[3px] mb-0.5"
                                            :class="ev._isPreview ? 'border-dashed opacity-60' : ''"
                                            :style="wkChipStyle(ev, tech)"
                                            @click.stop="!isPanning && openEventMenu($event, ev)">
                                            <span class="text-[11px] font-semibold text-gray-800 truncate block leading-snug">{{ ev.title }}</span>
                                            <span v-if="ev.ville" class="text-[10px] text-gray-500 truncate block">{{ ev.ville }}</span>
                                        </div>
                                    </div>
                                    <div v-if="isOverloaded(tech.id, day.date, 'afternoon')" class="absolute bottom-0.5 right-0.5 text-[9px] text-red-500"><i class="fas fa-exclamation-triangle"></i></div>
                                    <button v-if="!readonly"     class="flex-shrink-0 w-6 h-6 rounded-full border border-dashed border-gray-300 bg-white text-gray-400 flex items-center justify-center cursor-pointer text-[11px] mx-auto mb-2.5 transition-all hover:bg-indigo-500 hover:text-white hover:border-indigo-500 hover:scale-110 hover:shadow-sm"
                                        @click.stop="!isPanning && (multiSlots ? toggleSlot(tech.id, day.date, 'afternoon') : $emit('new-event', { techId: tech.id, date: day.date, slot: 'afternoon' }))"><i class="fas fa-plus"></i></button>
                                </div>
                            </div>
                            <!-- Bouton journée entière -->
                            <button v-if="multiSlots && !readonly"
                                class="w-full border-none cursor-pointer flex items-center justify-center gap-1 px-1.5 py-[5px] text-[10px] font-bold uppercase tracking-[0.3px] border-t border-gray-200 flex-shrink-0 transition-all"
                                :class="isSlotSelected(tech.id, day.date, 'morning') && isSlotSelected(tech.id, day.date, 'afternoon') ? 'bg-indigo-600 text-white hover:bg-red-500' : 'bg-gray-100 text-indigo-500 hover:bg-indigo-600 hover:text-white'"
                                @click.stop="!isPanning && toggleFullDay(tech.id, day.date)">
                                <i class="fas fa-plus"></i><span>Journée</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- ══════════════════ VUE MOIS (desktop) ══════════════════ -->
        <div v-if="viewMode === 'month' && !isMobile" class="flex-1 overflow-y-auto p-3.5 bg-gray-100">
            <div class="grid gap-1 mb-1" :style="{ gridTemplateColumns: `repeat(${monthColCount}, 1fr)` }">
                <div v-for="wd in monthWeekdayLabels" :key="wd" class="text-center text-[10.5px] font-bold text-gray-400 py-1.5">{{ wd }}</div>
            </div>
            <div class="grid gap-1" :style="{ gridTemplateColumns: `repeat(${monthColCount}, 1fr)` }">
                <div v-for="(cell, i) in monthCells" :key="'mc-'+i"
                    class="bg-white border rounded-[10px] min-h-[88px] p-[7px] relative overflow-hidden transition-all"
                    :class="[
                        !cell ? 'bg-transparent !border-transparent pointer-events-none' : '',
                        cell && isTodayDate(cell.date) ? 'bg-blue-50 border-blue-300' : cell ? 'border-gray-200' : '',
                        cell && cell.date === selectedDate ? '!border-blue-500 shadow-[0_0_0_2px_rgba(59,130,246,0.15)]' : '',
                        cell && cell.isWeekend ? '!bg-gray-50' : '',
                        cell && hasCellOverload(cell.date) ? '!border-red-300' : '',
                        cell ? 'cursor-pointer hover:border-blue-500 hover:-translate-y-px hover:shadow-md' : ''
                    ]"
                    @click="cell && selectDate(cell.date); cell && (viewMode = 'day')">
                    <template v-if="cell">
                        <div class="text-[13px] font-bold inline-flex items-center justify-center min-w-[24px] min-h-[24px] mb-1"
                            :class="isTodayDate(cell.date) ? 'bg-blue-500 text-white rounded-full w-6 h-6 text-xs' : 'text-gray-700'">{{ cell.day }}</div>
                        <div class="flex flex-col gap-[3px]">
                            <div v-for="tech in filteredTechnicians.slice(0, 4)" :key="'mctr-'+tech.id" class="flex items-center gap-1">
                                <div class="w-1.5 h-1.5 rounded-full flex-shrink-0" :style="{ background: tech.color || '#3b82f6' }"></div>
                                <div class="flex gap-0.5 flex-1">
                                    <div v-for="slot in halfDaySlots" :key="'mcsp-'+slot.id"
                                        class="flex-1 h-2 rounded-sm transition-all"
                                        :class="[
                                            isOverloaded(tech.id, cell.date, slot.id) ? 'animate-pulse' : '',
                                            multiSlots && isSlotSelected(tech.id, cell.date, slot.id) ? 'shadow-[0_0_0_1.5px_#4f46e5]' : ''
                                        ]"
                                        :style="pipStyle(tech, cell.date, slot.id)">
                                    </div>
                                </div>
                            </div>
                            <div v-if="filteredTechnicians.length > 4" class="text-[9px] text-gray-400">+{{ filteredTechnicians.length - 4 }}</div>
                        </div>
                        <div v-if="hasCellOverload(cell.date)" class="absolute top-[5px] right-[22px] text-[10px] text-red-500"><i class="fas fa-exclamation-triangle"></i></div>
                        <div v-if="hasCellPreview(cell.date)" class="absolute top-[5px] right-[6px] text-[10px] text-amber-500"><i class="fas fa-eye"></i></div>
                    </template>
                </div>
            </div>
        </div>

        <!-- ══════════════════ DATE PICKER PORTAL ══════════════════ -->
        <teleport to="body" v-if="datePickerOpen">
            <div class="dp-portal bg-white border border-gray-200 rounded-2xl shadow-2xl p-3.5 w-[260px] font-sans" :style="datePickerStyle" @mousedown.stop>
                <template v-if="viewMode === 'month' && !isMobile">
                    <div class="flex items-center justify-between mb-3">
                        <button class="w-[26px] h-[26px] rounded-lg border border-gray-200 flex items-center justify-center cursor-pointer text-gray-500 text-[10px] hover:bg-indigo-50 hover:border-indigo-400 hover:text-indigo-500 transition-colors" @click="dpYear--"><i class="fas fa-chevron-left"></i></button>
                        <span class="text-sm font-bold text-gray-900">{{ dpYear }}</span>
                        <button class="w-[26px] h-[26px] rounded-lg border border-gray-200 flex items-center justify-center cursor-pointer text-gray-500 text-[10px] hover:bg-indigo-50 hover:border-indigo-400 hover:text-indigo-500 transition-colors" @click="dpYear++"><i class="fas fa-chevron-right"></i></button>
                    </div>
                    <div class="grid grid-cols-3 gap-1.5">
                        <button v-for="(m, i) in monthNames" :key="i"
                            class="px-1 py-2.5 rounded-lg border text-[12.5px] font-medium cursor-pointer transition-all"
                            :class="dpYear === currentYear && i === currentMonth ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-transparent text-gray-600 border-transparent hover:bg-indigo-50 hover:text-indigo-600'"
                            @click="pickMonth(i)">{{ m }}</button>
                    </div>
                </template>
                <template v-else>
                    <div class="flex items-center justify-between mb-3">
                        <button class="w-[26px] h-[26px] rounded-lg border border-gray-200 flex items-center justify-center cursor-pointer text-gray-500 text-[10px] hover:bg-indigo-50 hover:border-indigo-400 transition-colors" @click="dpPrevMonth"><i class="fas fa-chevron-left"></i></button>
                        <div class="flex gap-1">
                            <select class="text-xs font-semibold text-gray-900 border border-gray-200 rounded-md px-1.5 py-1 bg-gray-50 cursor-pointer focus:outline-none focus:border-indigo-400" v-model="dpMonth">
                                <option v-for="(m, i) in monthNames" :key="i" :value="i">{{ m }}</option>
                            </select>
                            <select class="text-xs font-semibold text-gray-900 border border-gray-200 rounded-md px-1.5 py-1 bg-gray-50 cursor-pointer focus:outline-none focus:border-indigo-400" v-model="dpYear">
                                <option v-for="y in dpYearRange" :key="y" :value="y">{{ y }}</option>
                            </select>
                        </div>
                        <button class="w-[26px] h-[26px] rounded-lg border border-gray-200 flex items-center justify-center cursor-pointer text-gray-500 text-[10px] hover:bg-indigo-50 hover:border-indigo-400 transition-colors" @click="dpNextMonth"><i class="fas fa-chevron-right"></i></button>
                    </div>
                    <div class="grid grid-cols-7 text-center mb-1">
                        <span v-for="wd in ['L','M','M','J','V','S','D']" :key="wd+'_'+Math.random()" class="text-[10px] font-bold text-gray-400 py-1">{{ wd }}</span>
                    </div>
                    <div class="grid grid-cols-7 gap-0.5">
                        <span v-for="_ in dpFirstDow" :key="'e'+_" class="h-8"></span>
                        <button v-for="day in dpDaysInMonth" :key="day"
                            class="h-8 rounded-[7px] border-none text-[12.5px] font-medium cursor-pointer transition-all"
                            :class="[
                                isPickerToday(day) ? 'text-indigo-600 font-extrabold' : 'text-gray-700',
                                isPickerSelected(day) ? '!bg-indigo-600 !text-white' : 'hover:bg-indigo-50 hover:text-indigo-600',
                                viewMode === 'week' && isDayInSelectedWeek(day) && !isPickerSelected(day) ? 'bg-indigo-50 text-indigo-600 rounded-none' : ''
                            ]"
                            @click="pickDay(day)">{{ day }}</button>
                    </div>
                    <div class="mt-2.5 text-center">
                        <button class="text-xs font-semibold text-indigo-600 bg-indigo-50 border-none rounded-md px-3.5 py-1.5 cursor-pointer hover:bg-indigo-100 transition-colors" @click="goToday">Aujourd'hui</button>
                    </div>
                </template>
            </div>
        </teleport>

        <!-- ══ Event action menu ══ -->
        <template v-if="eventMenu">
            <!-- Overlay -->
            <div
                class="fixed inset-0 z-[9998]"
                @mousedown.stop="closeEventMenu"
            ></div>

            <!-- Dropdown -->
            <div
                :class="['fixed z-[9999] bg-white rounded-2xl border border-gray-200 shadow-lg w-[260px]', eventMenuClosing ? 'opacity-0 scale-95' : 'opacity-100 scale-100']"
                :style="{ left: eventMenu.x + 'px', top: eventMenu.y + 'px', transition: 'opacity 0.15s ease, transform 0.2s cubic-bezier(0.34,1.56,0.64,1)', transformOrigin: 'top left' }"
                @mousedown.stop
            >

                <!-- Infos -->
                <div class="px-4 pt-3.5" :class="canOpenChantier || canEditEvent ? 'border-b border-gray-100 pb-3' : ''">
                    <div class="text-[13px] font-bold text-gray-900 truncate">{{ eventMenu.ev.title }}</div>
                    <div v-if="eventMenu.ev.ville" class="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span class="text-[11px] text-gray-400 truncate">{{ eventMenu.ev.ville }}</span>
                    </div>
                    <div v-if="eventMenu.ev.notes" class="text-[11px] text-gray-500 mt-1.5 leading-relaxed line-clamp-2 italic">{{ eventMenu.ev.notes }}</div>
                </div>

                <!-- Actions -->
                <div class="p-1.5 flex flex-col">
                <div v-if="canOpenChantier" class="flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors" @click="openChantier(eventMenu.ev)">
                    <i class="fas fa-hard-hat text-gray-400 text-[12px] w-4 text-center"></i>
                    <span class="text-[13px] font-medium text-gray-700">Ouvrir le chantier</span>
                </div>
                <div v-if="canEditEvent">
                    <div class="flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors" @click="moveEvent(eventMenu.ev)">
                        <i class="fas fa-arrows-alt text-gray-400 text-[12px] w-4 text-center"></i>
                        <span class="text-[13px] font-medium text-gray-700">Déplacer</span>
                    </div>
                    <div class="h-px bg-gray-100 my-1"></div>
                    <div class="flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer hover:bg-red-50 transition-colors" @click="cancelEvent(eventMenu.ev)">
                        <i class="fas fa-times text-red-400 text-[12px] w-4 text-center"></i>
                        <span class="text-[13px] font-medium text-red-500">Annuler l'intervention</span>
                    </div>
                </div>
                </div>

            </div>
        </template>

        <!-- ══ Modale déplacement ══ -->
        <modal name="modal-move-intervention" height="auto" :scrollable="true" :adaptive="true" width="90%" @closed="closeMoveModal">
            <div class="p-8 overflow-y-auto max-h-[100vh]">

                <!-- Header -->
                <div class="flex items-center justify-between mb-6 pb-4 border-b">
                <div>
                    <h2 class="text-xl font-bold flex items-center gap-2">
                    <i class="fas fa-arrows-alt text-red-600"></i>
                    Déplacer l'intervention
                    </h2>
                    <div class="flex items-center gap-2 mt-2">
                    <div :class="['flex items-center gap-1.5 text-xs font-semibold', moveStep === 1 ? 'text-gray-900' : 'text-gray-400']">
                        <div :class="['w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold', moveStep === 1 ? 'bg-gray-900 text-white' : 'bg-green-500 text-white']">
                        <i v-if="moveStep === 2" class="fas fa-check text-[9px]"></i>
                        <span v-else>1</span>
                        </div>
                        Choisir un créneau
                    </div>
                    <div class="w-8 h-px bg-gray-200"></div>
                    <div :class="['flex items-center gap-1.5 text-xs font-semibold', moveStep === 2 ? 'text-gray-900' : 'text-gray-400']">
                        <div :class="['w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold', moveStep === 2 ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-500']">2</div>
                        Confirmer
                    </div>
                    </div>
                </div>
                <button @click="closeMoveModal" class="text-gray-400 hover:text-red-500 transition-colors">
                    <i class="fas fa-times text-xl"></i>
                </button>
                </div>

                <template v-if="movingEvent">

                <!-- ── STEP 1 : Planning ── -->
                <div v-if="moveStep === 1">
                    <p class="text-sm text-gray-500 mb-4">Cliquez sur le créneau souhaité pour y déplacer l'intervention.</p>
                    <div class="border border-gray-200 rounded-2xl overflow-hidden" style="height: 520px;">
                    <planning-half-day
                        :multi-slots="false"
                        :readonly="false"
                        :preview-intervention="{ ...movingEvent, _isPreview: true }"
                        @new-event="onMoveSlotPicked"
                        :can-open-chantier="false"
                        :can-edit-event="false"
                    />
                    </div>
                </div>

                <!-- ── STEP 2 : Récap + motif ── -->
                <div v-if="moveStep === 2">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-8">

                    <!-- Gauche : créneau sélectionné -->
                    <div>
                        <p class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Nouveau créneau</p>
                        <div class="p-5 bg-slate-50 border border-slate-200 rounded-2xl flex flex-col gap-4">
                        <div class="flex items-center gap-3">
                            <div>
                                <div class="text-sm font-bold text-gray-900">{{ moveSelectedSlot.techName }}</div>
                            </div>
                        </div>
                        <div class="flex items-center gap-2 text-sm font-semibold text-gray-700 capitalize">
                            <i class="fas fa-calendar-alt text-green-500"></i>
                            {{ formatDateFr(moveSelectedSlot.date) }} - <span class="text-xs text-gray-500 mt-0.5">{{ moveSelectedSlot.slot === 'morning' ? 'Matin' : 'Après-midi' }}</span>
                        </div>
                        </div>
                    </div>

                    <!-- Droite : motif -->
                    <div>
                        <p class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Motif du déplacement</p>

                        <!-- Catégorie -->
                        <div class="mb-4">
                        <label class="block text-sm font-semibold text-gray-700 mb-2">Catégorie <span class="text-red-500">*</span></label>
                        <div class="flex flex-wrap gap-2">
                            <button
                            v-for="(cat, key) in moveMotifCategories" :key="key"
                            :class="['px-3 py-1.5 rounded-lg text-sm font-medium border transition-all', moveCategorieSelectionnee === key ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400']"
                            @click="moveCategorieSelectionnee = key; moveMotif = ''"
                            >{{ cat.label }}</button>
                        </div>
                        </div>

                        <!-- Motifs pré-remplis -->
                        <div v-if="moveCategorieSelectionnee" class="mb-4">
                        <label class="block text-sm font-semibold text-gray-700 mb-2">Motif <span class="text-red-500">*</span></label>
                        <div class="flex flex-col gap-1.5">
                            <button
                            v-for="motif in moveMotifCategories[moveCategorieSelectionnee].motifs" :key="motif"
                            :class="['w-full text-left px-4 py-2.5 rounded-xl text-sm border transition-all', moveMotif === motif ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400']"
                            @click="moveMotif = motif"
                            >{{ motif }}</button>
                        </div>
                        </div>

                        <!-- Motif libre -->
                        <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-2">
                            Précision
                            <span v-if="!moveCategorieSelectionnee" class="text-red-500">*</span>
                            <span v-else class="text-gray-400 font-normal">(optionnel)</span>
                        </label>
                        <textarea
                            v-model="moveMotif"
                            rows="4"
                            placeholder="Ou saisissez un motif libre..."
                            class="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-gray-400 resize-none transition"
                        ></textarea>
                        </div>
                    </div>
                    </div>

                    <!-- Boutons en bas -->
                    <div class="flex items-center justify-center gap-3 mt-8 pt-6 border-t border-gray-100">
                    <button
                        @click="moveStep = 1"
                        class="flex items-center gap-2 px-5 py-5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition"
                    >
                        <i class="fas fa-arrow-left text-xs"></i> Retour
                    </button>
                    <button
                        :disabled="!moveMotif.trim()"
                        @click="submitMove"
                        class="flex items-center gap-2 px-6 py-5 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <i class="fas fa-check"></i> Confirmer le déplacement
                    </button>
                    </div>

                </div>
                </template>
            </div>
        </modal>

        <modal name="modal-cancel-intervention" height="auto" :scrollable="true" :adaptive="true" width="90%">
            <div class="p-8 overflow-y-auto max-h-[100vh]">

                <!-- Header -->
                <div class="flex items-center justify-between mb-6 pb-4 border-b">
                <h2 class="text-xl font-bold flex items-center gap-2">
                    <i class="fas fa-times-circle text-red-600"></i>
                    Annuler l'intervention
                </h2>
                <button @click="closeCancelModal" class="text-gray-400 hover:text-red-500 transition-colors">
                    <i class="fas fa-times text-xl"></i>
                </button>
                </div>

                <template v-if="cancellingEvent">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-8">

                    <!-- Gauche : récap intervention -->
                    <div>
                    <p class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Intervention concernée</p>
                    <div class="p-5 bg-red-50 border border-red-200 rounded-2xl flex flex-col gap-4">
                        <div class="flex items-center gap-3">
                        <div class="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                            :style="{ background: techColorForEvent(cancellingEvent) }">
                            {{ initials(techNameForEvent(cancellingEvent)) }}
                        </div>
                        <div class="flex-1 min-w-0">
                            <div class="text-sm font-bold text-gray-900 truncate">{{ cancellingEvent.title }}</div>
                            <div class="text-xs text-gray-500 mt-0.5">{{ techNameForEvent(cancellingEvent) }}</div>
                        </div>
                        </div>
                        <div v-if="cancellingEvent.ville" class="flex items-center gap-2 text-sm text-gray-600">
                        <i class="fas fa-map-marker-alt text-red-400 w-4 text-center"></i>
                        {{ cancellingEvent.ville }}
                        </div>
                        <div class="flex items-center gap-2 text-sm font-semibold text-gray-700 capitalize">
                        <i class="fas fa-calendar-alt text-red-400 w-4 text-center"></i>
                        {{ formatDateFr(cancellingEvent.date) }}
                        </div>
                        <div class="flex items-center gap-2 text-sm text-gray-600">
                        <i class="fas fa-clock text-red-400 w-4 text-center"></i>
                        {{ cancellingEvent.slot === 'morning' ? 'Matin' : 'Après-midi' }}
                        </div>
                        <span :class="['self-start text-[11px] font-semibold px-2.5 py-1 rounded-full', statusBadgeClass(cancellingEvent.status)]">
                        {{ statusLabel(cancellingEvent.status) }}
                        </span>
                    </div>

                    <!-- Avertissement -->
                    <div class="mt-4 flex items-start gap-3 p-4 bg-orange-50 border border-orange-200 rounded-xl">
                        <i class="fas fa-exclamation-triangle text-orange-500 mt-0.5 flex-shrink-0"></i>
                        <p class="text-sm text-orange-700">Cette action annulera l'intervention du planning. Elle restera visible dans l'historique.</p>
                    </div>
                    </div>

                    <!-- Droite : motif -->
                    <div>
                    <p class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Motif de l'annulation</p>

                    <!-- Catégorie -->
                    <div class="mb-4">
                        <label class="block text-sm font-semibold text-gray-700 mb-2">Catégorie <span class="text-red-500">*</span></label>
                        <div class="flex flex-wrap gap-2">
                        <button
                            v-for="(cat, key) in cancelMotifCategories" :key="key"
                            :class="['px-3 py-1.5 rounded-lg text-sm font-medium border transition-all', cancelCategorieSelectionnee === key ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400']"
                            @click="cancelCategorieSelectionnee = key; cancelMotif = ''"
                        >{{ cat.label }}</button>
                        </div>
                    </div>

                    <!-- Motifs pré-remplis -->
                    <div v-if="cancelCategorieSelectionnee" class="mb-4">
                        <label class="block text-sm font-semibold text-gray-700 mb-2">Motif <span class="text-red-500">*</span></label>
                        <div class="flex flex-col gap-1.5">
                        <button
                            v-for="motif in cancelMotifCategories[cancelCategorieSelectionnee].motifs" :key="motif"
                            :class="['w-full text-left px-4 py-2.5 rounded-xl text-sm border transition-all', cancelMotif === motif ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400']"
                            @click="cancelMotif = motif"
                        >{{ motif }}</button>
                        </div>
                    </div>

                    <!-- Motif libre -->
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-2">
                        Précision
                        <span v-if="!cancelCategorieSelectionnee" class="text-red-500">*</span>
                        <span v-else class="text-gray-400 font-normal">(optionnel)</span>
                        </label>
                        <textarea
                        v-model="cancelMotif"
                        rows="4"
                        placeholder="Ou saisissez un motif libre..."
                        class="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-gray-400 resize-none transition"
                        ></textarea>
                    </div>
                    </div>
                </div>

                <!-- Boutons -->
                <div class="flex items-center gap-3 mt-8 pt-6 border-t border-gray-100">
                    <button
                    @click="closeCancelModal"
                    class="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition"
                    >
                    Fermer
                    </button>
                    <button
                    :disabled="!cancelMotif.trim()"
                    @click="submitCancel"
                    class="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                    <i class="fas fa-times mr-1"></i> Confirmer l'annulation
                    </button>
                </div>

                </template>
            </div>
        </modal>

    </div>
</template>

<script>
const MOBILE_BREAKPOINT = 768

export default {
    name: 'PlanningHalfDay',

    props: {
        heureDebutMatin:     { type: String,  default: '08:00' },
        heurefinMatin:       { type: String,  default: '12:00' },
        heureDebutApresMidi: { type: String,  default: '13:00' },
        heureFinApresMidi:   { type: String,  default: '17:00' },
        previewIntervention: { type: Object,  default: null },
        readonly:            { type: Boolean, default: false },
        multiSlots:          { type: Boolean, default: false },
        days:                { type: Array,   default: null },
        chantierId:          { type: [String, Number], default: null },
        clickable: { type: Boolean, default: true },
        canOpenChantier: { type: Boolean, default: true },
        canEditEvent:    { type: Boolean, default: true },
    },

    data() {
        const today = new Date()
        const pad   = n => String(n).padStart(2, '0')
        return {
            instanceId: Math.random().toString(36).slice(2, 7),

            isMobile:       false,
            technicians:    [],
            selectedDate:   `${today.getFullYear()}-${pad(today.getMonth()+1)}-${pad(today.getDate())}`,
            viewMode:       'week',
            filterTechId:   null,
            planningEvents: [],
            loading:        false,
            isPanning:      false,
            panStartX:      0,
            panScrollLeft:  0,
            selectOpen:     false,
            dropdownStyle:  {},
            _leaveTimer:    null,

            datePickerOpen:  false,
            datePickerStyle: {},
            dpMonth: new Date().getMonth(),
            dpYear:  new Date().getFullYear(),
            monthNames: ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'],

            selectedSlotKeys: [],

            // Mobile : tab actif ('recap' | 'list') par slot
            mobActiveTab: { morning: 'recap', afternoon: 'recap' },

            eventMenu: null,        // { ev, x, y } | null
            eventMenuClosing: false,
            
            moveMotif: '',
            moveCategorieSelectionnee: '',
            moveMotifCategories: {
            client: {
                label: 'Client',
                motifs: ['Client absent', 'Client a reporté', 'Client injoignable', 'Mauvaise adresse', 'Accès impossible']
            },
            technicien: {
                label: 'Technicien',
                motifs: ['Maladie', 'Absence imprévue', 'Panne véhicule', 'Surcharge planning']
            },
            technique: {
                label: 'Technique',
                motifs: ['Pièce manquante', 'Outils manquants', 'Problème technique imprévu']
            },
            organisation: {
                label: 'Planning',
                motifs: ['Erreur de planification', 'Double réservation', 'Priorité modifiée', 'Urgence ajoutée']
            },
            },

            movingEvent: null,
            cancelMotif: '',
            cancelCategorieSelectionnee: '',
            cancelMotifCategories: {
                client: {
                    label: 'Client',
                    motifs: ['Client a annulé', 'Client injoignable', 'Doublon de demande', 'Problème résolu sans intervention']
                },
                technique: {
                    label: 'Technique',
                    motifs: ['Intervention impossible techniquement', 'Appareil irréparable', 'Hors garantie refusée']
                },
                organisation: {
                    label: 'Organisation',
                    motifs: ['Erreur de saisie', 'Annulation commerciale', 'Devis refusé']
                },
            },
            moveStep: 1,
            moveSelectedSlot: null,

            cancellingEvent: null,
            cancelMotif: '',
            cancelCategorieSelectionnee: '',
            cancelMotifCategories: {
                client: {
                    label: 'Client',
                    motifs: ['Client a annulé', 'Client injoignable', 'Doublon de demande', 'Problème résolu sans intervention']
                },
                technique: {
                    label: 'Technique',
                    motifs: ['Intervention impossible techniquement', 'Appareil irréparable', 'Hors garantie refusée']
                },
                organisation: {
                    label: 'Organisation',
                    motifs: ['Erreur de saisie', 'Annulation commerciale', 'Devis refusé']
                },
                technicien: {
                    label: 'Technicien',
                    motifs: ['Maladie', 'Absence imprévue', 'Panne véhicule']
                },
            },
        }
    },

    computed: {
        halfDaySlots() {
            return [
                { id: 'morning',   label: 'Matin',      durationMins: this.timeDiffMins(this.heureDebutMatin, this.heurefinMatin) },
                { id: 'afternoon', label: 'Après-midi', durationMins: this.timeDiffMins(this.heureDebutApresMidi, this.heureFinApresMidi) },
            ]
        },
        filteredTechnicians() {
            return this.filterTechId ? this.technicians.filter(t => t.id === this.filterTechId) : this.technicians
        },
        mobileSingleTech() {
            if (!this.filterTechId) return null
            return this.technicians.find(t => t.id === this.filterTechId) || null
        },
        // ID du technicien actif en mobile (ou premier si un seul)
        mobTechId() {
            if (this.filterTechId) return this.filterTechId
            if (this.filteredTechnicians.length === 1) return this.filteredTechnicians[0].id
            return null
        },
        mobTechColor() {
            const t = this.technicians.find(t => t.id === this.mobTechId)
            return t ? t.color : '#3b82f6'
        },
        isToday() { return this.selectedDate === this.todayStr() },

        // Labels mobile date header
        mobDayNum() {
            return new Date(this.selectedDate + 'T00:00:00').getDate()
        },
        mobWeekday() {
            return new Date(this.selectedDate + 'T00:00:00')
                .toLocaleDateString('fr-FR', { weekday: 'long' })
                .replace(/^\w/, c => c.toUpperCase())
        },
        mobMonth() {
            return new Date(this.selectedDate + 'T00:00:00')
                .toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
        },

        weekDays() {
            const DAY_NAMES = ['dimanche','lundi','mardi','mercredi','jeudi','vendredi','samedi']
            const start = this.getWeekStart(this.selectedDate)
            const all = Array.from({ length: 7 }, (_, i) => {
                const d = new Date(start); d.setDate(d.getDate() + i)
                const pad = n => String(n).padStart(2, '0')
                return {
                    date: `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`,
                    num: d.getDate(),
                    shortName: d.toLocaleDateString('fr-FR', { weekday: 'short' }),
                    isWeekend: d.getDay() === 0 || d.getDay() === 6,
                    dayName: DAY_NAMES[d.getDay()],
                }
            })
            if (!this.days || this.days.length === 0) return all
            const filter = this.days.map(d => d.toLowerCase())
            return all.filter(d => filter.includes(d.dayName))
        },
        activeDayIndices() {
            if (!this.days || this.days.length === 0) return null
            const DAY_NAMES = ['dimanche','lundi','mardi','mercredi','jeudi','vendredi','samedi']
            return this.days.map(d => DAY_NAMES.indexOf(d.toLowerCase())).filter(i => i !== -1)
        },
        monthCells() {
            const d = new Date(this.selectedDate + 'T00:00:00')
            const year = d.getFullYear(), month = d.getMonth()
            const pad = n => String(n).padStart(2, '0')
            const dim = new Date(year, month + 1, 0).getDate()
            if (this.activeDayIndices) {
                const cells = []
                for (let day = 1; day <= dim; day++) {
                    const date = `${year}-${pad(month+1)}-${pad(day)}`
                    const dow = new Date(date + 'T00:00:00').getDay()
                    if (this.activeDayIndices.includes(dow)) cells.push({ day, date, isWeekend: [0, 6].includes(dow) })
                }
                return cells
            }
            const startDow = (new Date(year, month, 1).getDay() + 6) % 7
            const cells = Array(startDow).fill(null)
            for (let day = 1; day <= dim; day++) {
                const date = `${year}-${pad(month+1)}-${pad(day)}`
                cells.push({ day, date, isWeekend: [0, 6].includes(new Date(date + 'T00:00:00').getDay()) })
            }
            return cells
        },
        selectedTech() {
            return this.technicians.find(t => t.id === this.filterTechId) || null
        },
        dateRangeLabel() {
            const d = new Date(this.selectedDate + 'T00:00:00')
            if (this.viewMode === 'day') return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
            if (this.viewMode === 'week') {
                const days = this.weekDays
                if (!days || days.length === 0) return ''
                const start = new Date(days[0].date + 'T00:00:00')
                const end   = new Date(days[days.length - 1].date + 'T00:00:00')
                const fmt = { day: 'numeric', month: 'short' }
                return start.toLocaleDateString('fr-FR', fmt) + ' – ' + end.toLocaleDateString('fr-FR', { ...fmt, year: 'numeric' })
            }
            return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
        },
        dateRangeLabelMobile() {
            const d = new Date(this.selectedDate + 'T00:00:00')
            return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
        },
        currentYear()  { return new Date(this.selectedDate + 'T00:00:00').getFullYear() },
        currentMonth() { return new Date(this.selectedDate + 'T00:00:00').getMonth() },
        dpYearRange() {
            const y = new Date().getFullYear()
            return Array.from({ length: 12 }, (_, i) => y - 3 + i)
        },
        dpFirstDow() { return (new Date(this.dpYear, this.dpMonth, 1).getDay() + 6) % 7 },
        dpDaysInMonth() { return new Date(this.dpYear, this.dpMonth + 1, 0).getDate() },
        monthColCount() { return this.activeDayIndices ? this.activeDayIndices.length : 7 },
        monthWeekdayLabels() {
            const ALL = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim']
            if (!this.days || this.days.length === 0) return ALL
            const DAY_NAMES = ['dimanche','lundi','mardi','mercredi','jeudi','vendredi','samedi']
            return this.days
                .map(d => DAY_NAMES.indexOf(d.toLowerCase()))
                .filter(i => i !== -1)
                .sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7))
                .map(i => ALL[(i + 6) % 7])
        },
        selectedSlots() {
            return this.selectedSlotKeys.map(key => {
                const [techId, date, slotId] = key.split('|')
                const tech = this.technicians.find(t => String(t.id) === String(techId))
                return { date, account_id: techId, slot: slotId, time: this.slotRemainingMins(techId, date, slotId), techName: tech ? tech.name : techId }
            })
        },
    },

    watch: {
        selectedDate() {
            this.fetchEvents()
            // Reset accordéons à chaque changement de jour
            this.mobActiveTab = { morning: 'recap', afternoon: 'recap' }
        },
        viewMode()     { this.fetchEvents() },
        selectedSlots(val) { this.$emit('slot-selection-change', val) },
        isMobile(val) {
            if (val) {
                this.viewMode = 'day'
                if (!this.filterTechId && this.technicians.length === 1) {
                    this.filterTechId = this.technicians[0].id
                }
            }
        },
        filterTechId() {
            // Reset accordéons au changement de tech
            this.mobActiveTab = { morning: 'recap', afternoon: 'recap' }
        },
    },

    methods: {
        // ── Mobile helpers ────────────────────────────────────────────
        mobEventCount(slotId) {
            if (!this.mobTechId) return 0
            return this.getEventsForCell(this.mobTechId, this.selectedDate, slotId).filter(e => !e._isPreview).length
        },
        setMobTab(slotId, tab) {
            this.mobActiveTab = { ...this.mobActiveTab, [slotId]: tab }
        },
        goTodayDirect() {
            const date = this.todayStr()
            this.selectDate(date)
            this.$emit('date-change', date)
        },

        // ── Tailwind helpers ──────────────────────────────────────────
        dayCellBg(techId, date, slotId) {
            if (this.isOverloaded(techId, date, slotId)) return 'bg-red-50'
            if (!this.isCellEmpty(techId, date, slotId)) return slotId === 'morning' ? 'bg-amber-50/60' : 'bg-blue-50/60'
            return slotId === 'morning' ? 'bg-amber-50/20' : 'bg-blue-50/20'
        },
        wkHalfBg(techId, date, slotId) {
            if (this.isOverloaded(techId, date, slotId)) return 'bg-red-50'
            if (!this.isCellEmpty(techId, date, slotId)) return slotId === 'morning' ? 'bg-amber-50' : 'bg-blue-50'
            return slotId === 'morning' ? 'bg-amber-50/30' : 'bg-blue-50/20'
        },
        evTypeTw(ev) {
            return ev.type === 'absence' || ev.type === 'conge' ? 'opacity-70' : ''
        },
        statusDotTw(status) {
            const map = { planned: 'bg-blue-500', in_progress: 'bg-amber-500', done: 'bg-green-500', cancelled: 'bg-red-500' }
            return map[status] || 'bg-gray-400'
        },
        pipStyle(tech, date, slotId) {
            const isOver = this.isOverloaded(tech.id, date, slotId)
            const hasEvs = this.getEventsForCell(tech.id, date, slotId).length > 0
            const hasPreview = this.hasCellPreviewForTech(date, tech.id, slotId)
            if (isOver) return { background: '#ef4444' }
            if (hasEvs) return { background: hasPreview ? '#f59e0b' : (tech.color || '#3b82f6') }
            return { background: '#e5e7eb' }
        },

        // ── Multi-slot ────────────────────────────────────────────────
        slotKey(techId, date, slotId) { return `${techId}|${date}|${slotId}` },
        isSlotSelected(techId, date, slotId) { return this.selectedSlotKeys.indexOf(this.slotKey(techId, date, slotId)) !== -1 },
        toggleSlot(techId, date, slotId) {
            if (!this.multiSlots) return
            const key = this.slotKey(techId, date, slotId)
            const idx = this.selectedSlotKeys.indexOf(key)
            if (idx === -1) this.selectedSlotKeys.push(key)
            else this.selectedSlotKeys.splice(idx, 1)
        },
        clearSelection() { this.selectedSlotKeys = [] },
        toggleFullDay(techId, date) {
            const both = this.isSlotSelected(techId, date, 'morning') && this.isSlotSelected(techId, date, 'afternoon')
            const self = this
            ;['morning', 'afternoon'].forEach(slot => {
                const key = self.slotKey(techId, date, slot)
                const idx = self.selectedSlotKeys.indexOf(key)
                if (both) { if (idx !== -1) self.selectedSlotKeys.splice(idx, 1) }
                else      { if (idx === -1) self.selectedSlotKeys.push(key) }
            })
        },
        slotRemainingMins(techId, date, slotId) {
            const cap  = this.slotCapacityMins(slotId)
            const used = this.cellTotalMins(techId, date, slotId)
            return Math.round(Math.max(0, cap - used) / 30) * 30
        },

        // ── Utilitaires ───────────────────────────────────────────────
        todayStr() {
            const t = new Date(), pad = n => String(n).padStart(2, '0')
            return `${t.getFullYear()}-${pad(t.getMonth()+1)}-${pad(t.getDate())}`
        },
        pad(n) { return String(n).padStart(2, '0') },
        isTodayDate(s) { return s === this.todayStr() },
        toDateStr(d) { return `${d.getFullYear()}-${this.pad(d.getMonth()+1)}-${this.pad(d.getDate())}` },
        getWeekStart(dateStr) {
            const d = new Date(dateStr + 'T00:00:00')
            d.setDate(d.getDate() - (d.getDay() + 6) % 7)
            return d
        },
        timeDiffMins(start, end) {
            const [sh, sm] = start.split(':').map(Number), [eh, em] = end.split(':').map(Number)
            return (eh * 60 + em) - (sh * 60 + sm)
        },
        selectDate(dateStr) {
            if (!dateStr) return
            this.selectedDate = dateStr
            this.$emit('date-change', dateStr)
        },
        offsetPeriod(delta) {
            const d = new Date(this.selectedDate + 'T00:00:00')
            if (this.viewMode === 'day' || this.isMobile) d.setDate(d.getDate() + delta)
            else if (this.viewMode === 'week')  d.setDate(d.getDate() + delta * 7)
            else if (this.viewMode === 'month') d.setMonth(d.getMonth() + delta)
            this.selectDate(this.toDateStr(d))
        },

        // ── Pan ───────────────────────────────────────────────────────
        onPanStart(e) {
            if (e.button !== 0) return
            const el = this.$refs.weekScroll
            if (!el) return
            e.preventDefault()
            this.isPanning    = false
            this.panStartX    = e.clientX
            this.panScrollLeft = el.scrollLeft
            document.addEventListener('mousemove', this._onPanMove)
            document.addEventListener('mouseup',   this._onPanEnd)
        },
        _onPanMove(e) {
            const dx = e.clientX - this.panStartX
            if (!this.isPanning && Math.abs(dx) > 5) this.isPanning = true
            if (this.isPanning) { const el = this.$refs.weekScroll; if (el) el.scrollLeft = this.panScrollLeft - dx }
        },
        _onPanEnd() {
            document.removeEventListener('mousemove', this._onPanMove)
            document.removeEventListener('mouseup',   this._onPanEnd)
            setTimeout(() => { this.isPanning = false }, 50)
        },

        // ── API ───────────────────────────────────────────────────────
        getDateRange() {
            if (this.viewMode === 'day' || this.isMobile) return { start: this.selectedDate, end: this.selectedDate }
            if (this.viewMode === 'week') {
                const s = this.getWeekStart(this.selectedDate), e = new Date(s); e.setDate(e.getDate() + 6)
                return { start: this.toDateStr(s), end: this.toDateStr(e) }
            }
            const d = new Date(this.selectedDate + 'T00:00:00'), year = d.getFullYear(), month = d.getMonth()
            return { start: this.toDateStr(new Date(year, month, 1)), end: this.toDateStr(new Date(year, month + 1, 0)) }
        },
        async fetchEvents() {
            this.loading = true
            try {
                const { start, end } = this.getDateRange()
                const response = await this.$request.post('/interventions/on-period', {
                    date_debut: start, date_fin: end, chantier_id: this.chantierId || 0
                })
                this.planningEvents = response.map(e => ({
                    id:                e.id,
                    technicianId:      e.technicien_id,
                    date:              e.date,
                    slot:              e.moment_day || null,
                    title:             e.title || (e.intervention?.client?.nom ? (e.intervention?.client?.nom + ' ' + e.intervention?.client?.prenom) : '') || e.type || e.titre || '—',
                    type:              e.type || 'intervention',
                    ville:             e.intervention?.adresse?.ville || null,
                    color:             e.color || null,
                    status:            e.statut || e.intervention?.statut?.libelle || 'planned',
                    estimatedDuration: e.intervention?.duree_estimee || e.estimation || e.duree || null,
                    interventionId:    e.intervention?.id ?? null,
                    chantierId:        e.chantier_id ?? null,
                }))
                this.$emit('period-change', { start, end, type: this.viewMode })
            } catch (err) {
                console.error('Erreur récupération planning :', err)
            } finally {
                this.loading = false
            }
        },
        async getTechniciens() {
            try {
                const response = await this.$request.get('/techniciens')
                this.technicians = response.map(t => ({
                    id:    t.id,
                    name:  t.prenom + ' ' + t.nom,
                    role:  'Technicien',
                    color: t.color || '#3b82f6',
                }))
                if (this.isMobile && this.technicians.length === 1) {
                    this.filterTechId = this.technicians[0].id
                }
            } catch (e) {
                console.error('Erreur récupération techniciens :', e)
            }
        },

        // ── Events ────────────────────────────────────────────────────
        getEventsForCell(techId, date, slotId) {
            const evs = this.planningEvents.filter(ev =>
                ev.slot !== null && ev.technicianId === techId && ev.date === date && (ev.slot === slotId || ev.slot === 'full_day')
            )
            if (this.previewIntervention) {
                const p = this.previewIntervention
                if ((!p.date || p.date === date) && (!p.technicianId || p.technicianId === techId) && (!p.slot || p.slot === slotId || p.slot === 'full_day'))
                    evs.push({ ...p, _isPreview: true })
            }
            return evs
        },
        isCellEmpty(techId, date, slotId) { return this.getEventsForCell(techId, date, slotId).filter(e => !e._isPreview).length === 0 },
        cellTotalMins(techId, date, slotId) {
            return this.getEventsForCell(techId, date, slotId).filter(e => !e._isPreview).reduce((s, e) => s + (parseInt(e.estimatedDuration) || 0), 0)
        },
        slotCapacityMins(slotId)  { return (this.halfDaySlots.find(s => s.id === slotId) || {}).durationMins || 240 },
        isOverloaded(techId, date, slotId) { return this.cellTotalMins(techId, date, slotId) > this.slotCapacityMins(slotId) },
        techSlotCapacityPercent(techId, date, slotId) {
            const cap = this.slotCapacityMins(slotId)
            if (!cap) return 0
            return Math.min(100, Math.round(this.cellTotalMins(techId, date, slotId) / cap * 100))
        },
        techSlotCapacityColor(techId, date, slotId) {
            const p = this.techSlotCapacityPercent(techId, date, slotId)
            if (p >= 100 || this.isOverloaded(techId, date, slotId)) return '#ef4444'
            if (p >= 60)  return '#f59e0b'
            if (p > 0)    return '#22c55e'
            return 'transparent'
        },
        satBarStyle(techId, date, slotId) {
            const isOver = this.isOverloaded(techId, date, slotId)
            const p      = this.techSlotCapacityPercent(techId, date, slotId)
            const color  = this.techSlotCapacityColor(techId, date, slotId)
            return { width: (isOver ? 100 : p) + '%', background: color }
        },
        hasBusyDay(techId, date) { return this.halfDaySlots.some(s => this.getEventsForCell(techId, date, s.id).length > 0) },
        hasCellOverload(date) { return this.filteredTechnicians.some(t => this.halfDaySlots.some(s => this.isOverloaded(t.id, date, s.id))) },
        hasCellPreview(date) { return !!this.previewIntervention && (!this.previewIntervention.date || this.previewIntervention.date === date) },
        hasCellPreviewForTech(date, techId, slotId) {
            if (!this.previewIntervention) return false
            const p = this.previewIntervention
            return (!p.date || p.date === date) && (!p.technicianId || p.technicianId === techId) && (!p.slot || p.slot === slotId || p.slot === 'full_day')
        },

        // ── Affichage ─────────────────────────────────────────────────
        initials(name) { return (name || '').split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2) },
        formatDuration(mins) {
            if (!mins) return ''
            const h = Math.floor(mins / 60), m = mins % 60
            return h ? (m ? `${h}h${String(m).padStart(2,'0')}` : `${h}h`) : `${m}min`
        },
        cellEventStyle(ev, tech) {
            const c = ev._isPreview ? '#f59e0b' : (ev.color || tech.color || '#3b82f6')
            return { '--ev-color': c, background: c + '14', borderColor: c + '55' }
        },
        wkChipStyle(ev, tech) {
            const c = ev._isPreview ? '#f59e0b' : (ev.color || tech.color || '#3b82f6')
            return {
                background: c + '12',
                borderLeftColor: c,
                borderLeftWidth: '3px',
                borderLeftStyle: 'solid',
                borderRadius: '7px',
            }
        },
        checkMobile() { this.isMobile = window.innerWidth < MOBILE_BREAKPOINT },
        onEnter() {
            if (this.isMobile) return
            clearTimeout(this._leaveTimer)
            this.selectOpen = true
            this.$nextTick(() => this.updateDropdownPos())
        },
        onLeave() {
            if (this.isMobile) return
            this._leaveTimer = setTimeout(() => { this.selectOpen = false }, 120)
        },
        updateDropdownPos() {
            const trigger = this.$refs.csTrigger
            if (!trigger) return
            const rect = trigger.getBoundingClientRect()
            this.dropdownStyle = { position: 'fixed', top: rect.bottom + 4 + 'px', left: rect.left + 'px', minWidth: rect.width + 'px', zIndex: 99999 }
        },
        toggleDatePicker() {
            this.datePickerOpen = !this.datePickerOpen
            if (this.datePickerOpen) {
                const d = new Date(this.selectedDate + 'T00:00:00')
                this.dpMonth = d.getMonth()
                this.dpYear  = d.getFullYear()
                this.$nextTick(() => this.updateDatePickerPos())
            }
        },
        updateDatePickerPos() {
            const trigger = this.isMobile
                ? this.$refs.mobileDateBtn
                : this.$el.querySelector('.tb-date-trigger')
            if (!trigger) return
            const rect = trigger.getBoundingClientRect()
            if (this.isMobile) {
                this.datePickerStyle = { position: 'fixed', top: rect.bottom + 6 + 'px', left: '50%', transform: 'translateX(-50%)', zIndex: 99999, width: 'calc(100vw - 24px)', maxWidth: '320px' }
            } else {
                this.datePickerStyle = { position: 'fixed', top: rect.bottom + 4 + 'px', left: rect.left + 'px', zIndex: 99999 }
            }
        },
        closeDatePicker() { this.datePickerOpen = false },
        dpPrevMonth() { if (this.dpMonth === 0) { this.dpMonth = 11; this.dpYear-- } else this.dpMonth-- },
        dpNextMonth() { if (this.dpMonth === 11) { this.dpMonth = 0; this.dpYear++ } else this.dpMonth++ },
        isPickerToday(day) {
            const t = new Date()
            return day === t.getDate() && this.dpMonth === t.getMonth() && this.dpYear === t.getFullYear()
        },
        isPickerSelected(day) {
            const s = new Date(this.selectedDate + 'T00:00:00')
            return day === s.getDate() && this.dpMonth === s.getMonth() && this.dpYear === s.getFullYear()
        },
        isDayInSelectedWeek(day) {
            const cell = new Date(this.dpYear, this.dpMonth, day)
            const ws   = this.getWeekStart(this.selectedDate)
            const we   = new Date(ws); we.setDate(we.getDate() + 6)
            return cell >= ws && cell <= we
        },
        pickDay(day) {
            const pad = n => String(n).padStart(2, '0')
            const date = `${this.dpYear}-${pad(this.dpMonth + 1)}-${pad(day)}`
            this.selectDate(date); this.$emit('date-change', date); this.datePickerOpen = false
        },
        pickMonth(monthIndex) {
            const pad = n => String(n).padStart(2, '0')
            const date = `${this.dpYear}-${pad(monthIndex + 1)}-01`
            this.selectDate(date); this.$emit('date-change', date); this.datePickerOpen = false
        },
        goToday() {
            const date = this.todayStr()
            this.selectDate(date); this.$emit('date-change', date); this.datePickerOpen = false
        },
        _onClickOutside(e) {
            if (this.datePickerOpen && !e.target.closest('.dp-portal') && !e.target.closest('[data-date-nav]')) {
                this.datePickerOpen = false
            }
        },
        openEventMenu(event, ev) {
            if (!this.clickable) return
            event.stopPropagation()
            if (this.eventMenu) { this.closeEventMenu(); return }
            const rect = event.currentTarget.getBoundingClientRect()
            const ddW = 300, ddH = 320
            let x = rect.left
            let y = rect.bottom + 6
            if (x + ddW > window.innerWidth - 12) x = window.innerWidth - ddW - 12
            if (y + ddH > window.innerHeight - 12) y = rect.top - ddH - 6
            this.eventMenu = { ev, x, y }
            this.eventMenuClosing = false
        },

        closeEventMenu() {
            this.eventMenuClosing = true
            setTimeout(() => {
                this.eventMenu = null
                this.eventMenuClosing = false
            }, 150)
        },

        openChantier(ev) {
            this.closeEventMenu()
            this.$emit('event-click', ev)
        },

        moveEvent(ev) {
            this.closeEventMenu()
            this.movingEvent = ev
            this.moveMotif = ''
            this.moveCategorieSelectionnee = ''
            this.moveStep = 1
            this.moveSelectedSlot = null
            this.$modal.show('modal-move-intervention')
        },

        onMoveSlotPicked({ techId, date, slot }) {
            const tech = this.technicians.find(t => String(t.id) === String(techId))
            this.moveSelectedSlot = { techId, date, slot, techName: tech ? tech.name : techId, techColor: tech ? tech.color : '#6366f1' }
            this.moveStep = 2
        },

        async submitMove() {
            if (!this.moveMotif.trim()) return
            try {
                await this.$request.post('/interventions/events/move', {
                    event_id:      this.movingEvent.id,
                    technicien_id: this.moveSelectedSlot.techId,
                    date:          this.moveSelectedSlot.date,
                    moment_day:    this.moveSelectedSlot.slot,
                    motif:         this.moveMotif,
                })
                this.closeMoveModal()
                this.fetchEvents()
            } catch (err) {
                console.error('Erreur déplacement :', err)
            }
        },

        cancelEvent(ev) {
            this.closeEventMenu()
            this.cancellingEvent = ev
            this.cancelMotif = ''
            this.cancelCategorieSelectionnee = ''
            this.$modal.show('modal-cancel-intervention')
        },

        closeCancelModal() {
            this.$modal.hide('modal-cancel-intervention')
            this.cancellingEvent = null
            this.cancelMotif = ''
            this.cancelCategorieSelectionnee = ''
        },

        async submitCancel() {
            if (!this.cancelMotif.trim()) return
            try {
                await this.$request.post('/interventions/events/cancel-calendar', {
                event_id: this.cancellingEvent.id,
                motif:    this.cancelMotif,
                })
                this.closeCancelModal()
                this.fetchEvents()
            } catch (err) {
                console.error('Erreur annulation :', err)
            }
        },

        techNameForEvent(ev) {
            const t = this.technicians.find(t => t.id === ev.technicianId)
            return t ? t.name : '—'
        },
        techColorForEvent(ev) {
            const t = this.technicians.find(t => t.id === ev.technicianId)
            return t ? t.color : '#6366f1'
        },
        statusLabel(status) {
            const map = { planned: 'Planifié', in_progress: 'En cours', done: 'Terminé', cancelled: 'Annulé' }
            return map[status] || status || 'Planifié'
        },
        statusBadgeClass(status) {
            const map = {
                planned:     'bg-blue-50 text-blue-600',
                in_progress: 'bg-amber-50 text-amber-600',
                done:        'bg-green-50 text-green-600',
                cancelled:   'bg-red-50 text-red-500',
            }
            return map[status] || 'bg-gray-100 text-gray-500'
        },
        statusDotColor(status) {
            const map = {
                planned:     'bg-blue-400',
                in_progress: 'bg-amber-400',
                done:        'bg-green-400',
                cancelled:   'bg-red-400',
            }
            return map[status] || 'bg-gray-400'
        },

        moveEvent(ev) {
            this.closeEventMenu()
            this.movingEvent = ev
            this.moveMotif = ''
            this.$modal.show('modal-move-intervention')
        },

        formatDateFr(dateStr) {
            if (!dateStr) return ''
            const d = new Date(dateStr + 'T00:00:00')
            return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
        },
        closeMoveModal() {
            this.$modal.hide('modal-move-intervention')
            this.movingEvent = null
            this.moveMotif = ''
            this.moveCategorieSelectionnee = ''
            this.moveStep = 1
            this.moveSelectedSlot = null
        },
    },

    async mounted() {
        this.checkMobile()
        this._resizeListener = () => this.checkMobile()
        window.addEventListener('resize', this._resizeListener)
        await this.getTechniciens()
        this.fetchEvents()
        document.addEventListener('mousedown', this._onClickOutside)
        document.addEventListener('touchstart', this._onClickOutside, { passive: true })
        this.$eventHub.$on('refresh-planning', this.fetchEvents)
        this.$eventHub.$on('clear-selection', this.clearSelection)
    },
    beforeDestroy() {
        window.removeEventListener('resize', this._resizeListener)
        document.removeEventListener('mousemove', this._onPanMove)
        document.removeEventListener('mouseup',   this._onPanEnd)
        document.removeEventListener('mousedown', this._onClickOutside)
        document.removeEventListener('touchstart', this._onClickOutside)
    },
}
</script>

<style scoped>
@media (max-width: 767px) {
    @import url('https://fonts.googleapis.com/css2?family=Figtree:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    /* ════════════════════════════════════════════════════
    STYLES DESKTOP — inchangés
    ════════════════════════════════════════════════════ */

    /* ── Shared ── */
    .cev-stripe { width: 4px; flex-shrink: 0; background: var(--ev-color, #3b82f6); }
    .cev-body { flex: 1; padding: 5px 8px; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
    .cev-title { font-size: 12px; font-weight: 600; color: #1a1d27; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .cev-location, .cev-duration { font-size: 10.5px; color: #6b7280; display: flex; align-items: center; gap: 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .cev-duration { font-family: 'JetBrains Mono', monospace; font-size: 9.5px; color: #9ba3b2; }
    .cev-sub-icon { font-size: 8.5px; opacity: 0.5; flex-shrink: 0; }
    .preview-chip { display: inline-flex; align-items: center; gap: 3px; background: #fef3c7; color: #92400e; border: 1px solid #fde68a; font-size: 9px; font-weight: 700; padding: 1px 5px; border-radius: 3px; text-transform: uppercase; }

    .fade-enter-active, .fade-leave-active { transition: opacity 0.2s; }
    .fade-enter, .fade-leave-to { opacity: 0; }

    .ms-badge-slide-enter-active, .ms-badge-slide-leave-active { transition: all 0.2s ease; }
    .ms-badge-slide-enter, .ms-badge-slide-leave-to { opacity: 0; transform: scale(0.85); }

    /* Date picker */
    .dp-portal { font-family: 'Figtree', sans-serif; }
    .tb-date-trigger { display: flex; align-items: center; gap: 7px; padding: 5px 11px; border-radius: 8px; border: 1.5px solid #e2e8f0; background: #fff; cursor: pointer; font-family: 'Figtree', sans-serif; font-size: 12.5px; font-weight: 600; color: #1e293b; transition: all 0.15s; white-space: nowrap; }
    .tb-date-trigger:hover { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); }

    /* ═══════════════════════════════════════
    MOBILE — styles garantis
    ═══════════════════════════════════════ */

    /* Header */
    .mob-header {
        box-shadow: 0 1px 0 #f0f2f5;
    }
    .mob-header-line {
        height: 1px;
        background: #f0f2f5;
    }

    /* Date row */
    .mob-date-row {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 20px 20px 16px;
    }
    .mob-arrow-btn {
        width: 48px;
        height: 48px;
        border-radius: 16px;
        background: #f4f6f9;
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        color: #555;
        cursor: pointer;
        flex-shrink: 0;
        transition: background 0.15s;
        -webkit-tap-highlight-color: transparent;
    }
    .mob-arrow-btn:active { background: #e8edf4; }

    .mob-date-center {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2px;
        background: none;
        border: none;
        cursor: pointer;
        padding: 4px 0;
        -webkit-tap-highlight-color: transparent;
    }
    .mob-date-month-label {
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.12em;
        color: #9ca3af;
        text-transform: capitalize;
    }
    .mob-date-day-num {
        font-size: 40px;
        font-weight: 900;
        color: #111827;
        line-height: 1;
        font-family: 'Figtree', sans-serif;
    }
    .mob-date-weekday {
        font-size: 14px;
        font-weight: 600;
        color: #374151;
        text-transform: capitalize;
    }

    /* Aujourd'hui */
    .mob-today-row {
        display: flex;
        justify-content: center;
        padding-bottom: 12px;
    }
    .mob-today-btn {
        padding: 8px 20px;
        border-radius: 50px;
        background: #111827;
        color: #fff;
        font-size: 13px;
        font-weight: 600;
        border: none;
        cursor: pointer;
        font-family: 'Figtree', sans-serif;
        -webkit-tap-highlight-color: transparent;
    }
    .mob-today-btn:active { background: #374151; }

    /* Chips */
    .mob-chips-row {
        display: flex;
        gap: 8px;
        overflow-x: auto;
        padding: 0 20px 16px;
        scrollbar-width: none;
        -webkit-overflow-scrolling: touch;
    }
    .mob-chips-row::-webkit-scrollbar { display: none; }

    .mob-chip {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 16px;
        border-radius: 50px;
        border: 1.5px solid #e5e7eb;
        background: #f9fafb;
        color: #6b7280;
        font-size: 13px;
        font-weight: 600;
        white-space: nowrap;
        flex-shrink: 0;
        cursor: pointer;
        font-family: 'Figtree', sans-serif;
        transition: all 0.15s;
        -webkit-tap-highlight-color: transparent;
    }
    .mob-chip:active { transform: scale(0.95); }
    .mob-chip-active {
        background: #111827;
        border-color: #111827;
        color: #fff;
    }
    .mob-chip-dot {
        width: 22px;
        height: 22px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 9px;
        font-weight: 800;
        color: #fff;
        flex-shrink: 0;
    }

    /* Badge multi-slot */
    .mob-ms-bar {
        display: flex;
        align-items: center;
        gap: 10px;
        margin: 0 20px 12px;
        padding: 12px 16px;
        border-radius: 16px;
        background: #111827;
        color: #fff;
        font-size: 13px;
        font-weight: 600;
        font-family: 'Figtree', sans-serif;
    }
    .mob-ms-clear {
        margin-left: auto;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: rgba(255,255,255,0.15);
        border: none;
        color: #fff;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        -webkit-tap-highlight-color: transparent;
    }

    /* Scroll principal */
    .mob-scroll {
        flex: 1;
        overflow-y: auto;
        background: #f4f6f9;
        -webkit-overflow-scrolling: touch;
    }

    /* Section label */
    .mob-section-label {
        padding: 24px 20px 12px;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: #9ca3af;
        font-family: 'Figtree', sans-serif;
    }

    /* Liste techs */
    .mob-tech-list {
        margin: 0 16px;
        background: #fff;
        border-radius: 20px;
        overflow: hidden;
        box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    }
    .mob-tech-row {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 16px 20px;
        cursor: pointer;
        border-bottom: 1px solid #f3f4f6;
        -webkit-tap-highlight-color: transparent;
        transition: background 0.1s;
    }
    .mob-tech-row:last-child { border-bottom: none; }
    .mob-tech-row:active { background: #f9fafb; }
    .mob-tech-avatar {
        width: 46px;
        height: 46px;
        border-radius: 14px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-size: 15px;
        font-weight: 800;
        flex-shrink: 0;
        font-family: 'Figtree', sans-serif;
    }
    .mob-tech-name {
        font-size: 15px;
        font-weight: 700;
        color: #111827;
        font-family: 'Figtree', sans-serif;
    }
    .mob-tech-slots {
        display: flex;
        gap: 16px;
        margin-top: 4px;
        font-size: 13px;
        color: #9ca3af;
        font-family: 'Figtree', sans-serif;
    }
    .mob-tech-arrow {
        font-size: 12px;
        color: #d1d5db;
        margin-left: auto;
        flex-shrink: 0;
    }
    .mob-tech-info { flex: 1; min-width: 0; }

    /* Carte demi-journée */
    .mob-slot-card {
        margin: 16px 16px 0;
        background: #fff;
        border-radius: 20px;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0,0,0,0.07);
    }

    /* En-tête slot */
    .mob-slot-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 20px 20px 16px;
    }
    .mob-slot-title {
        font-size: 20px;
        font-weight: 900;
        color: #111827;
        font-family: 'Figtree', sans-serif;
        letter-spacing: -0.3px;
    }
    .mob-slot-hours {
        font-size: 13px;
        color: #9ca3af;
        font-weight: 500;
        margin-top: 2px;
        font-family: 'Figtree', sans-serif;
    }

    /* Badges */
    .mob-badge {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 6px 12px;
        border-radius: 50px;
        font-size: 12px;
        font-weight: 700;
        font-family: 'Figtree', sans-serif;
    }
    .mob-badge-red  { background: #fef2f2; color: #ef4444; }
    .mob-badge-green { background: #f0fdf4; color: #16a34a; }

    /* Tabs pill */
    .mob-tabs {
        display: flex;
        margin: 0 20px 16px;
        background: #f3f4f6;
        border-radius: 14px;
        padding: 4px;
    }
    .mob-tab {
        flex: 1;
        padding: 10px 8px;
        border-radius: 10px;
        border: none;
        background: transparent;
        font-size: 13px;
        font-weight: 600;
        color: #9ca3af;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        font-family: 'Figtree', sans-serif;
        transition: all 0.15s;
        -webkit-tap-highlight-color: transparent;
    }
    .mob-tab-active {
        background: #fff;
        color: #111827;
        box-shadow: 0 1px 4px rgba(0,0,0,0.1);
    }
    .mob-tab-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        font-size: 11px;
        font-weight: 700;
        background: #e5e7eb;
        color: #6b7280;
    }
    .mob-tab-badge-active {
        background: #111827;
        color: #fff;
    }

    /* Récap */
    .mob-recap {
        padding: 0 20px 20px;
    }
    .mob-recap-empty {
        text-align: center;
        color: #9ca3af;
        font-size: 14px;
        padding: 16px 0;
        font-family: 'Figtree', sans-serif;
    }
    .mob-stats {
        display: flex;
        align-items: stretch;
        background: #f9fafb;
        border-radius: 16px;
        overflow: hidden;
    }
    .mob-stat {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 18px 8px;
        gap: 6px;
    }
    .mob-stat-num {
        font-size: 26px;
        font-weight: 900;
        color: #111827;
        line-height: 1;
        font-family: 'Figtree', sans-serif;
    }
    .mob-stat-lbl {
        font-size: 11px;
        font-weight: 500;
        color: #9ca3af;
        text-align: center;
        line-height: 1.2;
        font-family: 'Figtree', sans-serif;
    }
    .mob-stat-sep {
        width: 1px;
        background: #e5e7eb;
        align-self: stretch;
        margin: 12px 0;
    }

    /* Liste événements */
    .mob-ev-list {
        overflow-y: auto;
        max-height: 280px;
        margin: 0 20px 16px;
        border-radius: 14px;
        border: 1px solid #f3f4f6;
        background: #fff;
    }
    .mob-ev-empty {
        text-align: center;
        color: #9ca3af;
        font-size: 14px;
        padding: 28px 16px;
        font-family: 'Figtree', sans-serif;
    }
    .mob-ev-item {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 14px 16px;
        border-bottom: 1px solid #f9fafb;
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
        transition: background 0.1s;
    }
    .mob-ev-item:last-child { border-bottom: none; }
    .mob-ev-item:active { background: #f9fafb; }
    .mob-ev-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        flex-shrink: 0;
    }
    .mob-ev-body { flex: 1; min-width: 0; }
    .mob-ev-title {
        font-size: 15px;
        font-weight: 600;
        color: #111827;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-family: 'Figtree', sans-serif;
    }
    .mob-ev-sub {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 3px;
        font-size: 13px;
        color: #9ca3af;
        font-family: 'Figtree', sans-serif;
    }
    .mob-ev-sep { color: #d1d5db; }
    .mob-ev-arrow {
        font-size: 10px;
        color: #d1d5db;
        flex-shrink: 0;
    }

    /* Bouton action */
    .mob-slot-footer {
        padding: 4px 20px 20px;
    }
    .mob-add-btn {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        padding: 16px;
        border-radius: 16px;
        border: 2px dashed #e5e7eb;
        background: transparent;
        font-size: 14px;
        font-weight: 600;
        color: #9ca3af;
        cursor: pointer;
        font-family: 'Figtree', sans-serif;
        -webkit-tap-highlight-color: transparent;
        transition: all 0.15s;
    }
    .mob-add-btn:active { background: #f9fafb; border-color: #d1d5db; color: #6b7280; }
    .mob-add-icon {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: #f3f4f6;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        color: #9ca3af;
        flex-shrink: 0;
    }
    .mob-select-btn {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        padding: 16px;
        border-radius: 16px;
        border: 2px solid #e5e7eb;
        background: #fff;
        font-size: 14px;
        font-weight: 700;
        color: #374151;
        cursor: pointer;
        font-family: 'Figtree', sans-serif;
        -webkit-tap-highlight-color: transparent;
        transition: all 0.15s;
    }
    .mob-select-btn:active { transform: scale(0.98); }
    .mob-select-btn-on {
        background: #111827;
        border-color: #111827;
        color: #fff;
        box-shadow: 0 4px 16px rgba(0,0,0,0.2);
    }
}

</style>